import { Drawer, Box, Typography, IconButton, Chip, CircularProgress, Alert, Collapse, Paper } from '@mui/material';
import { X, Clock, ArrowRight, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useSubmissionEmailTimeline } from '@lingaai/shared-hooks';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import { useState } from 'react';

interface EmailTimelineDrawerProps {
  submissionId: string | null;
  open: boolean;
  onClose: () => void;
}

const getEmailTypeLabel = (sourceType: string): string => {
  const labels: Record<string, string> = {
    outbound_initial: 'Initial Request',
    email_body: 'Response',
    outbound_received: 'Acknowledgment',
    outbound_followup: 'Follow-up Request',
    outbound_reminder: 'Reminder',
    outbound_summary: 'Summary',
  };
  return labels[sourceType] || sourceType;
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export function EmailTimelineDrawer({ submissionId, open, onClose }: EmailTimelineDrawerProps) {
  const { data: timeline, isLoading, error } = useSubmissionEmailTimeline(submissionId);
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const toggleExpanded = (entryId: string) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500 } },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 64,
          }}
        >
          <Typography variant="h6">Communication Timeline</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load email timeline'}</Alert>}

          {timeline && timeline.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No email interactions found
            </Typography>
          )}

          {timeline && timeline.length > 0 && (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 23,
                  top: 16,
                  bottom: 16,
                  width: 2,
                  bgcolor: 'divider',
                }}
              />

              {/* Timeline entries */}
              {timeline.map((entry) => {
                const isInbound = entry.source_type === 'email_body';
                const datetime = entry.email_received_at || entry.email_sent_at || entry.created_at;

                return (
                  <Box key={entry.id} sx={{ position: 'relative', pl: 6, pb: 3 }}>
                    {/* Icon with circle background */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 14,
                        top: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: isInbound ? 'primary.main' : 'grey.400',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {isInbound ? <ArrowLeft size={12} strokeWidth={2} /> : <ArrowRight size={12} strokeWidth={2} />}
                    </Box>

                    {/* Content */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2">{getEmailTypeLabel(entry.source_type)}</Typography>
                        {entry.email_status && <Chip label={entry.email_status} size="small" color={entry.email_status === 'sent' ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />}
                      </Box>

                      {entry.email_subject && (
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          {entry.email_subject}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Clock size={12} strokeWidth={1} />
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(datetime)}
                          </Typography>
                        </Box>
                        {isInbound && entry.email_from && (
                          <Typography variant="caption" color="text.secondary">
                            • From {entry.email_from}
                          </Typography>
                        )}
                        {!isInbound && entry.email_to && (
                          <Typography variant="caption" color="text.secondary">
                            • To {entry.email_to}
                          </Typography>
                        )}
                        {(entry as any).extracted_text && (
                          <IconButton size="small" onClick={() => toggleExpanded(entry.id)} sx={{ ml: 'auto' }}>
                            {expandedEntries.has(entry.id) ? <ChevronUp size={14} strokeWidth={1} /> : <ChevronDown size={14} strokeWidth={1} />}
                          </IconButton>
                        )}
                      </Box>

                      {/* Expandable email body */}
                      {(entry as any).extracted_text && (
                        <Collapse in={expandedEntries.has(entry.id)}>
                          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50', fontSize: '0.875rem' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {(entry as any).extracted_text}
                            </Typography>
                          </Paper>
                        </Collapse>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
